import { useEffect, useState } from "react";
import { Gauge } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { Card } from "@/components/ui/card";

const tone = (score) => (score >= 90 ? "bg-emerald-500" : score >= 75 ? "bg-amber-500" : "bg-rose-500");

// v2.8+E347 · per-person on-time / late / missed counts, last 90 days, worst first.
export const ReliabilityCard = ({ refreshKey }) => {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    adminApi.get("/admin/timesheets/reliability", { params: { days: 90 } })
      .then(({ data: d }) => { setData(d); setFailed(false); })
      .catch(() => { setData({ rows: [] }); setFailed(true); });
  }, [refreshKey]);

  if (!data) return null;
  const rows = data.rows || [];

  return (
    <Card className="p-4" data-testid="reliability-card">
      <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-1 flex items-center gap-1.5">
        <Gauge className="h-4 w-4"/>Reliability · last 90 days
      </div>
      <p className="text-[11px] text-zinc-400 mb-3">Late = clocked in 10+ min after the scheduled start. Missed = no clock-in within 30 min.</p>
      {failed ? (
        <div className="text-sm text-rose-500 py-4 text-center" data-testid="reliability-error">Couldn't load reliability right now — refresh to try again.</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-zinc-400 py-4 text-center" data-testid="reliability-empty">No past scheduled shifts in the last 90 days.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="reliability-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-500 border-b border-zinc-200 dark:border-zinc-800">
                <th className="py-2 pr-3">Staff</th>
                <th className="py-2 px-3 text-right">Shifts</th>
                <th className="py-2 px-3 text-right">On time</th>
                <th className="py-2 px-3 text-right">Late</th>
                <th className="py-2 px-3 text-right">Missed</th>
                <th className="py-2 pl-3 min-w-[140px]">Reliability</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.staff_id} className="border-b border-zinc-100 dark:border-zinc-900" data-testid={`reliability-row-${r.staff_id}`}>
                  <td className="py-2 pr-3 font-semibold">{r.staff_name}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{r.shifts}</td>
                  <td className="py-2 px-3 text-right tabular-nums text-emerald-600 dark:text-emerald-300">{r.on_time}</td>
                  <td className="py-2 px-3 text-right tabular-nums text-amber-600 dark:text-amber-300" data-testid={`reliability-late-${r.staff_id}`}>
                    {r.late}{r.late ? <span className="text-[10px] text-zinc-400"> · avg {r.avg_late_minutes}m</span> : null}
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums text-rose-600 dark:text-rose-300" data-testid={`reliability-missed-${r.staff_id}`}>{r.missed}</td>
                  <td className="py-2 pl-3">
                    <div className="flex items-center gap-2">
                      <div className="h-2 flex-1 rounded-full bg-zinc-200 dark:bg-zinc-800 overflow-hidden">
                        <div className={`h-full ${tone(r.score)}`} style={{ width: `${r.score}%` }}/>
                      </div>
                      <span className="text-xs font-bold tabular-nums w-10 text-right" data-testid={`reliability-score-${r.staff_id}`}>{r.score}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

export default ReliabilityCard;
